/* Locket Photo Maker — crop a photo to a locket shape and print it at true size.
 * Everything runs in the browser: the photo is never uploaded anywhere.
 * Exposes window.LocketPhotoMaker.{init, _test} (the _test helpers are pure functions
 * used by the local self-test; they have no side effects).
 */
(function () {
  "use strict";

  var DPI = 300;
  var MM_PER_IN = 25.4;
  var TAU = Math.PI * 2;
  var MAX_SRC_SIDE = 3000;          // downscale huge phone photos before editing
  var MIN_MM = 5, MAX_MM = 80;      // sane locket window range
  var GAP_MM = 3;                   // space between copies on the sheet
  var HEADER_MM = 12;               // scale-bar strip at the top of every sheet

  // Paper content areas (mm) after printer margins. 4x6 is a full-bleed photo print.
  var PAPERS = {
    letter: { w: 215.9, h: 279.4, margin: 10, css: "letter portrait" },
    a4:     { w: 210,   h: 297,   margin: 10, css: "A4 portrait" },
    photo:  { w: 152.4, h: 101.6, margin: 3,  css: "6in 4in" }
  };

  // Examples only — the page tells people to measure their own locket.
  var DEFAULT_SIZE = {
    mm: { heart: [20, 20], oval: [18, 24], round: [20, 20], rect: [20, 25] },
    in: { heart: [0.75, 0.75], oval: [0.7, 0.95], round: [0.75, 0.75], rect: [0.75, 1] }
  };
  function defaultMm(shape, unit) {
    var d = DEFAULT_SIZE[unit][shape];
    return [toMm(d[0], unit), toMm(d[1], unit)];
  }

  /* ---------- pure helpers (unit-tested) ---------- */

  function mmToPx(mm) { return Math.round(mm / MM_PER_IN * DPI); }

  function toMm(value, unit) { return unit === "in" ? value * MM_PER_IN : value; }

  function fromMm(mm, unit) {
    return unit === "in" ? Math.round(mm / MM_PER_IN * 100) / 100 : Math.round(mm * 10) / 10;
  }

  function clampMm(mm) {
    if (!isFinite(mm)) return null;
    return Math.min(MAX_MM, Math.max(MIN_MM, mm));
  }

  // How many tiles of tw x th (mm) fit on a paper; returns cols/rows/capacity.
  function layout(paperKey, tw, th) {
    var p = PAPERS[paperKey];
    var aw = p.w - 2 * p.margin, ah = p.h - 2 * p.margin - HEADER_MM;
    var cols = Math.max(0, Math.floor((aw + GAP_MM) / (tw + GAP_MM)));
    var rows = Math.max(0, Math.floor((ah + GAP_MM) / (th + GAP_MM)));
    return { cols: cols, rows: rows, capacity: cols * rows, areaW: aw, areaH: ah };
  }

  // Minimum zoom so a (rotated) image fully covers a box.
  function coverScale(imgW, imgH, rot, boxW, boxH) {
    var w = rot % 180 === 0 ? imgW : imgH;
    var h = rot % 180 === 0 ? imgH : imgW;
    return Math.max(boxW / w, boxH / h);
  }

  // Keep the image covering the box: limit the offset of the image centre.
  function clampOffset(off, imgW, imgH, rot, scale, boxW, boxH) {
    var w = (rot % 180 === 0 ? imgW : imgH) * scale;
    var h = (rot % 180 === 0 ? imgH : imgW) * scale;
    var mx = Math.max(0, (w - boxW) / 2), my = Math.max(0, (h - boxH) / 2);
    return { x: Math.min(mx, Math.max(-mx, off.x)), y: Math.min(my, Math.max(-my, off.y)) };
  }

  // Trace a shape filling the box (x, y, w, h). Heart = the same outline as the
  // IfShe product-page photo editor ("heart-locket"), normalised to the box.
  // keepPath = append to the current path (used for the even-odd dimming mask).
  function tracePath(c, shape, x, y, w, h, keepPath) {
    if (!keepPath) c.beginPath();
    if (shape === "heart") {
      // The curve's true extents are u -0.00034..1.00034, v 0.02492..1.0 (sampled);
      // remap so the drawn outline is exactly w x h — the size the customer measured.
      var X = function (u) { return x + (u + 0.00034) / 1.00068 * w; };
      var Y = function (v) { return y + (v - 0.02492) / 0.97508 * h; };
      c.moveTo(X(0.5), Y(1.0));
      c.bezierCurveTo(X(0.20), Y(0.72), X(-0.01), Y(0.50), X(0.0), Y(0.30));
      c.bezierCurveTo(X(0.0), Y(0.02), X(0.34), Y(-0.06), X(0.5), Y(0.124));
      c.bezierCurveTo(X(0.66), Y(-0.06), X(1.0), Y(0.02), X(1.0), Y(0.30));
      c.bezierCurveTo(X(1.01), Y(0.50), X(0.80), Y(0.72), X(0.5), Y(1.0));
      c.closePath();
    } else if (shape === "oval" || shape === "round") {
      c.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, TAU);
    } else {
      var r = Math.min(w, h) * 0.08;
      if (c.roundRect) { c.roundRect(x, y, w, h, r); } else { c.rect(x, y, w, h); }
    }
  }

  // --- PNG pHYs chunk so photo apps and print services read 300 DPI (true size) ---
  var CRC_TABLE = (function () {
    var t = new Uint32Array(256);
    for (var n = 0; n < 256; n++) {
      var c = n;
      for (var k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      t[n] = c >>> 0;
    }
    return t;
  })();

  function crc32(bytes) {
    var c = 0xffffffff;
    for (var i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
    return (c ^ 0xffffffff) >>> 0;
  }

  function withDpi(pngBytes, dpi) {
    var ppm = Math.round(dpi / 0.0254);
    var chunk = new Uint8Array(21);
    var dv = new DataView(chunk.buffer);
    dv.setUint32(0, 9);
    chunk.set([0x70, 0x48, 0x59, 0x73], 4);           // "pHYs"
    dv.setUint32(8, ppm); dv.setUint32(12, ppm); chunk[16] = 1;
    dv.setUint32(17, crc32(chunk.subarray(4, 17)));
    var at = 33;                                        // 8-byte signature + 25-byte IHDR
    var out = new Uint8Array(pngBytes.length + 21);
    out.set(pngBytes.subarray(0, at), 0);
    out.set(chunk, at);
    out.set(pngBytes.subarray(at), at + 21);
    return out;
  }

  // UI strings. English is the default; other languages pass opts.t (same keys).
  var EN = {
    placeholder: "Your photo will appear here",
    notImage: "That file isn't an image. Choose a JPG or PNG photo.",
    opening: "Opening your photo…",
    loaded: "Drag to position, pinch or use the slider to zoom.",
    badFormat: "This browser can't open that photo format. Try a JPG or PNG (on iPhone, choose the photo from Photos).",
    width: "Width", diameter: "Diameter",
    sizeError: "Enter the size in {unit} (between {min} and {max}).",
    tooBig: "That size is too big for {paper}. Pick a larger paper or a smaller size.",
    copyOne: "{n} copy", copyMany: "{n} copies",
    summary: "{count} of a {size} {shape} on {paper}{more}.",
    mostFit: " (that's the most that fit)",
    barMm: "10 mm", barIn: "1 inch",
    checkText: "Measure the bars: if they are true, your photos are true size.",
    noFile: "Sorry, this browser couldn't create the file.",
    savedHome: "Saved. Print it at 100% (Actual size), not Fit to page.",
    savedPhoto: "Saved. Order it as a standard 4×6 print.",
    noPrint: "Printing isn't available here. Use Download instead.",
    printHint: "In the print window, set Scale to 100% (Actual size).",
    filePrefix: "locket-photo", photoSuffix: "-4x6",
    fileShapes: { heart: "heart", oval: "oval", round: "round", rect: "rect" },
    cards: { heart: "Heart photo lockets", oval: "Oval photo lockets", round: "Round photo lockets", rect: "Rectangular photo lockets" },
    units: { mm: "mm", in: "in" },
    decimalComma: false
  };
  function fmt(str, vars) {
    return String(str).replace(/\{(\w+)\}/g, function (m, k) { return vars[k] !== undefined ? vars[k] : m; });
  }

  /* ---------- tool ---------- */

  function init(root, opts) {
    if (!root || root.__lpm) return;
    root.__lpm = true;
    opts = opts || {};
    var T = {}, k;
    for (k in EN) T[k] = EN[k];
    for (k in (opts.t || {})) T[k] = opts.t[k];
    // legacy option names used by the English section
    if (opts.placeholder) T.placeholder = opts.placeholder;
    if (opts.checkText) T.checkText = opts.checkText;
    if (opts.msgPhotoDownload) T.savedPhoto = opts.msgPhotoDownload;
    var num = function (v) { var s2 = String(v); return T.decimalComma ? s2.replace(".", ",") : s2; };
    var U = function (u) { return (T.units || {})[u] || u; };
    var readNum = function (el) { return parseFloat(String(el.value).replace(",", ".")); };
    var q = function (sel) { return root.querySelector(sel); };
    var qa = function (sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); };

    var st = {
      img: null, shape: "heart", unit: opts.unit === "in" ? "in" : "mm",
      wMm: 0, hMm: 0,
      rot: 0, zoom: 1, off: { x: 0, y: 0 },        // zoom is relative to cover scale
      copies: 4, paper: opts.paper || "letter", margin: true
    };

    var canvas = q("[data-lpm-canvas]");
    var ctx = canvas.getContext("2d");
    var fileInput = q("[data-lpm-file]");
    var drop = q("[data-lpm-drop]");
    var wInput = q("[data-lpm-w]"), hInput = q("[data-lpm-h]");
    var hField = q("[data-lpm-h-field]");
    var zoomInput = q("[data-lpm-zoom]");
    var copiesInput = q("[data-lpm-copies]");
    var marginInput = q("[data-lpm-margin]");
    var statusEl = q("[data-lpm-status]");
    var summaryEl = q("[data-lpm-summary]");
    var stepEdit = q("[data-lpm-edit]");
    var stepOutput = q("[data-lpm-output]");
    var printBtn = q("[data-lpm-print]"), dlBtn = q("[data-lpm-download]");
    var cta = q("[data-lpm-cta]");
    var d0 = defaultMm(st.shape, st.unit);
    st.wMm = d0[0]; st.hMm = d0[1];

    function say(msg, isError) {
      statusEl.textContent = msg || "";
      statusEl.classList.toggle("is-error", !!isError);
    }

    /* --- stage geometry: the shape window drawn inside the canvas --- */
    function stage() {
      var cw = canvas.width, ch = canvas.height, pad = cw * 0.1;
      var ar = st.wMm / st.hMm;
      var bw = cw - 2 * pad, bh = bw / ar;
      if (bh > ch - 2 * pad) { bh = ch - 2 * pad; bw = bh * ar; }
      return { x: (cw - bw) / 2, y: (ch - bh) / 2, w: bw, h: bh };
    }

    function bleedFactor() { return st.margin ? 1 + 2 / Math.min(st.wMm, st.hMm) : 1; } // 1 mm each side

    function baseScale(box) {
      var f = bleedFactor();
      return coverScale(st.img.width, st.img.height, st.rot, box.w * f, box.h * f);
    }

    function fixOffset(box) {
      var f = bleedFactor();
      st.off = clampOffset(st.off, st.img.width, st.img.height, st.rot,
        baseScale(box) * st.zoom, box.w * f, box.h * f);
    }

    function drawImageInto(c, box, k) {
      // k = output pixels per stage pixel (1 on screen)
      var s = baseScale(box) * st.zoom * k;
      c.save();
      c.translate((box.x + box.w / 2 + st.off.x) * k, (box.y + box.h / 2 + st.off.y) * k);
      c.rotate(st.rot * Math.PI / 180);
      c.drawImage(st.img, -st.img.width * s / 2, -st.img.height * s / 2, st.img.width * s, st.img.height * s);
      c.restore();
    }

    function render() {
      var dpr = Math.min(2, window.devicePixelRatio || 1);
      var cssW = canvas.clientWidth || 320;
      if (canvas.width !== Math.round(cssW * dpr)) {
        canvas.width = Math.round(cssW * dpr);
        canvas.height = Math.round(cssW * dpr);
      }
      var box = stage();
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "#f5f3f0";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      if (st.img) {
        fixOffset(box);
        drawImageInto(ctx, box, 1);
        // dim everything outside the shape
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, 0, canvas.width, canvas.height);
        tracePath(ctx, st.shape, box.x, box.y, box.w, box.h, true);
        ctx.fillStyle = "rgba(245,243,240,0.82)";
        ctx.fill("evenodd");
        ctx.restore();
      }
      tracePath(ctx, st.shape, box.x, box.y, box.w, box.h);
      ctx.lineWidth = Math.max(1.5, canvas.width / 220);
      ctx.strokeStyle = st.img ? "#b8956a" : "#c9c2b5";
      ctx.setLineDash(st.img ? [] : [8, 6]);
      ctx.stroke();
      ctx.setLineDash([]);
      if (!st.img) {
        ctx.fillStyle = "#8a8378";
        ctx.font = Math.round(canvas.width / 24) + "px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(T.placeholder, canvas.width / 2, canvas.height / 2);
      }
      updateSummary();
    }

    /* --- loading the photo (stays on the device) --- */
    function loadFile(file) {
      if (!file) return;
      if (!/^image\//.test(file.type) && !/\.(jpe?g|png|webp|gif|heic|heif)$/i.test(file.name)) {
        say(T.notImage, true);
        return;
      }
      say(T.opening);
      var url = URL.createObjectURL(file);
      var im = new Image();
      im.onload = function () {
        var side = Math.max(im.naturalWidth, im.naturalHeight);
        var src = im;
        if (side > MAX_SRC_SIDE) {
          var r = MAX_SRC_SIDE / side, cv = document.createElement("canvas");
          cv.width = Math.round(im.naturalWidth * r);
          cv.height = Math.round(im.naturalHeight * r);
          cv.getContext("2d").drawImage(im, 0, 0, cv.width, cv.height);
          src = cv;
        }
        st.img = src;   // a detached <img> reports width/height = natural size
        st.rot = 0; st.zoom = 1; st.off = { x: 0, y: 0 };
        zoomInput.value = "1";
        URL.revokeObjectURL(url);
        stepEdit.classList.remove("is-locked");
        stepOutput.classList.remove("is-locked");
        root.classList.add("has-photo");
        say(T.loaded);
        render();
      };
      im.onerror = function () {
        URL.revokeObjectURL(url);
        say(T.badFormat, true);
      };
      im.src = url;
    }

    fileInput.addEventListener("change", function () { loadFile(fileInput.files && fileInput.files[0]); });
    ["dragenter", "dragover"].forEach(function (ev) {
      drop.addEventListener(ev, function (e) { e.preventDefault(); drop.classList.add("is-over"); });
    });
    ["dragleave", "drop"].forEach(function (ev) {
      drop.addEventListener(ev, function (e) { e.preventDefault(); drop.classList.remove("is-over"); });
    });
    drop.addEventListener("drop", function (e) {
      loadFile(e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]);
    });

    /* --- shape + size --- */
    function syncSizeInputs() {
      wInput.value = fromMm(st.wMm, st.unit);
      hInput.value = fromMm(st.hMm, st.unit);
      hField.hidden = st.shape === "round";
      q("[data-lpm-w-label]").textContent = st.shape === "round" ? T.diameter : T.width;
      qa("[data-lpm-unit-label]").forEach(function (el) { el.textContent = U(st.unit); });
      wInput.step = hInput.step = st.unit === "in" ? "0.01" : "0.5";
    }

    qa("[data-lpm-shape]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        st.shape = btn.getAttribute("data-lpm-shape");
        qa("[data-lpm-shape]").forEach(function (b) { b.setAttribute("aria-pressed", b === btn ? "true" : "false"); });
        if (!root.classList.contains("size-touched")) {
          var d = defaultMm(st.shape, st.unit);
          st.wMm = d[0]; st.hMm = d[1];
        }
        if (st.shape === "round") st.hMm = st.wMm;
        st.zoom = 1; st.off = { x: 0, y: 0 }; zoomInput.value = "1";
        syncSizeInputs(); render(); syncCards();
      });
    });

    qa("[data-lpm-unit]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        st.unit = btn.getAttribute("data-lpm-unit");
        qa("[data-lpm-unit]").forEach(function (b) { b.setAttribute("aria-pressed", b === btn ? "true" : "false"); });
        syncSizeInputs(); updateSummary();
      });
    });

    function onSize() {
      var w = clampMm(toMm(readNum(wInput), st.unit));
      var h = st.shape === "round" ? w : clampMm(toMm(readNum(hInput), st.unit));
      if (w === null || h === null) {
        say(fmt(T.sizeError, { unit: U(st.unit), min: num(fromMm(MIN_MM, st.unit)), max: num(fromMm(MAX_MM, st.unit)) }), true);
        return;
      }
      root.classList.add("size-touched");
      st.wMm = w; st.hMm = h;
      say(st.img ? "" : statusEl.textContent);
      render();
    }
    wInput.addEventListener("input", onSize);
    hInput.addEventListener("input", onSize);
    wInput.addEventListener("change", syncSizeInputs);
    hInput.addEventListener("change", syncSizeInputs);

    /* --- positioning: drag, pinch, wheel, slider, rotate --- */
    var pointers = {};
    var pinch = null;
    function cssToCanvas() { return canvas.width / (canvas.clientWidth || canvas.width); }

    canvas.addEventListener("pointerdown", function (e) {
      if (!st.img) { fileInput.click(); return; }
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      try { canvas.setPointerCapture(e.pointerId); } catch (err) { /* capture is a nicety, not required */ }
      var ids = Object.keys(pointers);
      if (ids.length === 2) {
        var a = pointers[ids[0]], b = pointers[ids[1]];
        pinch = { d: Math.hypot(a.x - b.x, a.y - b.y), zoom: st.zoom };
      }
    });
    canvas.addEventListener("pointermove", function (e) {
      if (!st.img || !pointers[e.pointerId]) return;
      var prev = pointers[e.pointerId];
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      var ids = Object.keys(pointers);
      if (ids.length === 2 && pinch) {
        var a = pointers[ids[0]], b = pointers[ids[1]];
        setZoom(pinch.zoom * Math.hypot(a.x - b.x, a.y - b.y) / pinch.d);
      } else if (ids.length === 1) {
        var k = cssToCanvas();
        st.off.x += (e.clientX - prev.x) * k;
        st.off.y += (e.clientY - prev.y) * k;
        render();
      }
    });
    function endPointer(e) {
      delete pointers[e.pointerId];
      if (Object.keys(pointers).length < 2) pinch = null;
    }
    canvas.addEventListener("pointerup", endPointer);
    canvas.addEventListener("pointercancel", endPointer);
    canvas.addEventListener("wheel", function (e) {
      if (!st.img) return;
      e.preventDefault();
      setZoom(st.zoom * (e.deltaY < 0 ? 1.06 : 1 / 1.06));
    }, { passive: false });

    function setZoom(z) {
      st.zoom = Math.min(5, Math.max(1, z));
      zoomInput.value = String(st.zoom);
      render();
    }
    zoomInput.addEventListener("input", function () { setZoom(parseFloat(zoomInput.value)); });
    q("[data-lpm-rotate]").addEventListener("click", function () {
      if (!st.img) return;
      st.rot = (st.rot + 90) % 360; render();
    });
    q("[data-lpm-reset]").addEventListener("click", function () {
      if (!st.img) return;
      st.zoom = 1; st.off = { x: 0, y: 0 }; zoomInput.value = "1"; render();
    });
    q("[data-lpm-replace]").addEventListener("click", function () { fileInput.click(); });

    /* --- output options --- */
    qa("[data-lpm-paper]").forEach(function (btn) {
      if (btn.getAttribute("data-lpm-paper") === st.paper) btn.setAttribute("aria-pressed", "true");
      btn.addEventListener("click", function () {
        st.paper = btn.getAttribute("data-lpm-paper");
        qa("[data-lpm-paper]").forEach(function (b) { b.setAttribute("aria-pressed", b === btn ? "true" : "false"); });
        updateSummary();
      });
    });
    copiesInput.addEventListener("input", function () {
      var n = parseInt(copiesInput.value, 10);
      if (n >= 1) { st.copies = Math.min(48, n); updateSummary(); }
    });
    marginInput.addEventListener("change", function () { st.margin = marginInput.checked; render(); });

    function tileMm() {
      var m = st.margin ? 2 : 0;
      return { w: st.wMm + m, h: st.hMm + m };
    }

    function plan() {
      var t = tileMm();
      var L = layout(st.paper, t.w, t.h);
      return { tile: t, L: L, n: Math.min(st.copies, L.capacity) };
    }

    function updateSummary() {
      var p = plan();
      var size = num(fromMm(st.wMm, st.unit)) + (st.shape === "round" ? "" : " × " + num(fromMm(st.hMm, st.unit))) + " " + U(st.unit);
      var paperName = (opts.paperNames || {})[st.paper] || st.paper;
      var txt;
      if (p.L.capacity === 0) {
        txt = fmt(T.tooBig, { paper: paperName });
      } else {
        txt = fmt(T.summary, { count: fmt(p.n === 1 ? T.copyOne : T.copyMany, { n: p.n }), size: size,
          shape: (opts.shapeNames || {})[st.shape], paper: paperName, more: p.n < st.copies ? T.mostFit : "" });
      }
      summaryEl.textContent = txt;
      var ok = !!st.img && p.L.capacity > 0;
      printBtn.disabled = dlBtn.disabled = !ok;
    }

    /* --- building the true-size sheet --- */
    function buildSheet() {
      var p = plan();
      var paper = PAPERS[st.paper];
      var t = p.tile, L = p.L;
      var rows = Math.ceil(p.n / L.cols);
      var usedW = L.cols * t.w + (L.cols - 1) * GAP_MM;
      // +1 mm so the cut line on the last row/column is never clipped by the sheet edge
      var outWmm = st.paper === "photo" ? paper.w : Math.min(L.areaW, Math.max(usedW + 1, 100));
      var outHmm = st.paper === "photo" ? paper.h : Math.min(L.areaH + HEADER_MM, HEADER_MM + rows * t.h + (rows - 1) * GAP_MM + 1);
      var pad = st.paper === "photo" ? paper.margin : 0;
      var cv = document.createElement("canvas");
      cv.width = mmToPx(outWmm);
      cv.height = mmToPx(outHmm);
      var c = cv.getContext("2d");
      var px = function (mm) { return mm / MM_PER_IN * DPI; };
      c.fillStyle = "#fff";
      c.fillRect(0, 0, cv.width, cv.height);

      // scale check: 10 mm bar + 1 inch bar
      c.fillStyle = "#1a1a1a";
      c.font = Math.round(px(2.6)) + "px sans-serif";
      c.textBaseline = "middle";
      var y0 = px(pad + 3);
      c.fillRect(px(pad), y0, px(10), px(1.2));
      c.fillText(T.barMm, px(pad + 11.5), y0 + px(0.6));
      c.fillRect(px(pad + 28), y0, px(MM_PER_IN), px(1.2));
      c.fillText(T.barIn, px(pad + 28 + MM_PER_IN + 1.5), y0 + px(0.6));
      c.fillStyle = "#666";
      c.fillText(T.checkText, px(pad), y0 + px(5));

      var box = stage();                                  // on-screen window (stage px)
      var tileWpx = px(st.wMm);
      var k = tileWpx / box.w;                            // output px per stage px
      var bleed = st.margin ? 1 : 0;                      // mm each side
      for (var i = 0; i < p.n; i++) {
        var col = i % L.cols, row = Math.floor(i / L.cols);
        var tx = pad + col * (t.w + GAP_MM), ty = pad + HEADER_MM + row * (t.h + GAP_MM);
        var sx = px(tx + bleed), sy = px(ty + bleed);     // where the exact shape starts
        c.save();
        c.translate(sx - box.x * k, sy - box.y * k);
        // clip to the shape grown by the trim margin
        var gx = box.x - bleed / st.wMm * box.w, gy = box.y - bleed / st.hMm * box.h;
        var gw = box.w * (1 + 2 * bleed / st.wMm), gh = box.h * (1 + 2 * bleed / st.hMm);
        tracePath(c, st.shape, gx * k, gy * k, gw * k, gh * k);
        c.save();
        c.clip();
        drawImageInto(c, box, k);
        c.restore();
        // cut line at the exact size
        tracePath(c, st.shape, box.x * k, box.y * k, box.w * k, box.h * k);
        c.lineWidth = Math.max(1, px(0.15));
        c.strokeStyle = "rgba(90,90,90,0.9)";
        c.stroke();
        c.restore();
      }
      return { canvas: cv, wMm: outWmm, hMm: outHmm, n: p.n };
    }

    function fileName() {
      var s = fromMm(st.wMm, "mm") + (st.shape === "round" ? "" : "x" + fromMm(st.hMm, "mm"));
      return T.filePrefix + "-" + ((T.fileShapes || {})[st.shape] || st.shape) + "-" + s + "mm" + (st.paper === "photo" ? T.photoSuffix : "") + ".png";
    }

    function toPngBlob(cv, cb) {
      cv.toBlob(function (blob) {
        if (!blob) { cb(null); return; }
        var fr = new FileReader();
        fr.onload = function () {
          var bytes = withDpi(new Uint8Array(fr.result), DPI);
          cb(new Blob([bytes], { type: "image/png" }));
        };
        fr.readAsArrayBuffer(blob);
      }, "image/png");
    }

    dlBtn.addEventListener("click", function () {
      if (dlBtn.disabled) return;
      var sheet = buildSheet();
      toPngBlob(sheet.canvas, function (blob) {
        if (!blob) { say(T.noFile, true); return; }
        var a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = fileName();
        document.body.appendChild(a);
        a.click();
        setTimeout(function () { URL.revokeObjectURL(a.href); a.remove(); }, 4000);
        say(st.paper === "photo" ? T.savedPhoto : T.savedHome);
        showCta();
      });
    });

    printBtn.addEventListener("click", function () {
      if (printBtn.disabled) return;
      var sheet = buildSheet();
      var paper = PAPERS[st.paper];
      var holder = document.getElementById("lpm-print-root") || document.createElement("div");
      holder.id = "lpm-print-root";
      while (holder.firstChild) holder.removeChild(holder.firstChild);
      var im = new Image();
      im.alt = "";
      im.style.width = sheet.wMm + "mm";
      im.style.height = sheet.hMm + "mm";
      holder.appendChild(im);
      document.body.appendChild(holder);
      var pageStyle = document.getElementById("lpm-print-page") || document.createElement("style");
      pageStyle.id = "lpm-print-page";
      pageStyle.textContent = "@page{size:" + paper.css + ";margin:" + (st.paper === "photo" ? 0 : paper.margin) + "mm}";
      document.head.appendChild(pageStyle);
      im.onload = function () {
        document.documentElement.classList.add("lpm-printing");
        var done = function () {
          document.documentElement.classList.remove("lpm-printing");
          window.removeEventListener("afterprint", done);
        };
        window.addEventListener("afterprint", done);
        setTimeout(function () {
          try { window.print(); } catch (e) { say(T.noPrint, true); }
          setTimeout(done, 1000);
        }, 50);
      };
      im.src = sheet.canvas.toDataURL("image/png");
      say(T.printHint);
      showCta();
    });

    function showCta() { if (cta) cta.classList.add("is-highlight"); }

    // CTA cards follow the shape the visitor picked (fallback: keep the server default).
    var CARD_LABELS = T.cards;
    function syncCards() {
      var cards = qa("[data-lpm-card]");
      if (!cards.length) return;
      var match = cards.filter(function (c) { return c.getAttribute("data-lpm-card") === st.shape; });
      if (!match.length) return;
      cards.forEach(function (c) { c.hidden = match.indexOf(c) < 0; });
      var label = q("[data-lpm-cards-label]");
      if (label) label.textContent = CARD_LABELS[st.shape] || label.textContent;
    }

    window.addEventListener("resize", function () { render(); });
    syncSizeInputs();
    render();
  }

  window.LocketPhotoMaker = {
    init: init,
    _test: {
      mmToPx: mmToPx, toMm: toMm, fromMm: fromMm, clampMm: clampMm, layout: layout,
      coverScale: coverScale, clampOffset: clampOffset, crc32: crc32, withDpi: withDpi,
      PAPERS: PAPERS, HEADER_MM: HEADER_MM, GAP_MM: GAP_MM
    }
  };
})();
